/**
 * Portfolio Sync Status Page (Web)
 * Displays portfolio synchronization status and history
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this component only displays pre-calculated data.
 * All sync operations are handled by backend Portfolio Service.
 */

import { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { usePortfolioSync } from '@shared/features/portfolio';
import { useBrokerAccounts } from '@shared/features/broker-accounts';
import { AuthenticatedLayout } from '../../components/layout/AuthenticatedLayout';
import { formatDateTime } from '@shared/utils/formatters';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function PortfolioSyncPage() {
  const { accounts } = useBrokerAccounts();
  const [selectedBrokerAccount, setSelectedBrokerAccount] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);

  const {
    syncStatus,
    syncHistory,
    loading,
    error,
    syncInProgress,
    refreshStatus,
    refreshHistory,
    triggerSync,
  } = usePortfolioSync({
    brokerAccountId: selectedBrokerAccount || accounts[0]?.id || '',
    limit: 20,
    autoRefresh: false,
  });

  useEffect(() => {
    if (selectedBrokerAccount || accounts[0]?.id) {
      refreshStatus();
      refreshHistory();
    }
  }, [selectedBrokerAccount, accounts]);

  const handleRefresh = async () => {
    await refreshStatus();
    await refreshHistory();
  };

  const handleSync = async (scope: 'holdings_only' | 'transactions_only' | 'both' = 'both') => {
    await triggerSync(scope);
    // Refresh after sync
    setTimeout(() => {
      refreshStatus();
      refreshHistory();
    }, 2000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'failed':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'partial':
        return <ScheduleIcon color="warning" fontSize="small" />;
      default:
        return <CircularProgress size={16} />;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'success':
        return <Chip icon={getStatusIcon(status)} label="Success" color="success" size="small" />;
      case 'failed':
        return <Chip icon={getStatusIcon(status)} label="Failed" color="error" size="small" />;
      case 'partial':
        return <Chip icon={getStatusIcon(status)} label="Partial" color="warning" size="small" />;
      default:
        return <Chip icon={getStatusIcon(status)} label="Pending" color="default" size="small" />;
    }
  };

  return (
    <AuthenticatedLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Broker Account Selector */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <TextField
              select
              label="Broker Account"
              value={selectedBrokerAccount || accounts[0]?.id || ''}
              onChange={(e) => setSelectedBrokerAccount(e.target.value)}
              sx={{ 
                minWidth: 300,
                '& .MuiInputBase-root': { fontSize: '0.875rem' },
                '& .MuiInputLabel-root': { fontSize: '0.875rem' }
              }}
              size="small"
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id} sx={{ fontSize: '0.875rem' }}>
                  {account.account_name || account.broker_name}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={() => handleSync('both')}
                disabled={syncInProgress || !selectedBrokerAccount}
              >
                {syncInProgress ? 'Syncing...' : 'Sync Now'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Paper>

        {syncInProgress && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sync in progress...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Sync Actions */}
        {selectedBrokerAccount && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sync Options
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleSync('holdings_only')}
                    disabled={syncInProgress}
                  >
                    Sync Holdings Only
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleSync('transactions_only')}
                    disabled={syncInProgress}
                  >
                    Sync Transactions Only
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleSync('both')}
                    disabled={syncInProgress}
                  >
                    Sync Both
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Tabs for Status and History */}
        <Paper>
          <Tabs value={tabValue} onChange={(_event, newValue) => setTabValue(newValue)}>
            <Tab label="Recent Status" />
            <Tab label="Sync History" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : syncStatus.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Trigger</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="right">Holdings</TableCell>
                      <TableCell align="right">Transactions</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncStatus.map((sync, index) => (
                      <TableRow key={sync.id || index} hover>
                        <TableCell>{getStatusChip(sync.sync_status)}</TableCell>
                        <TableCell>
                          <Chip label={sync.sync_type || 'full'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sync.sync_trigger || 'manual'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {sync.sync_started_at
                            ? formatDateTime(sync.sync_started_at, { includeTime: true })
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {sync.sync_duration_ms !== undefined
                            ? `${(sync.sync_duration_ms / 1000).toFixed(2)}s`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {sync.holdings_synced_count ?? '—'}
                        </TableCell>
                        <TableCell align="right">
                          {sync.transactions_synced_count ?? '—'}
                        </TableCell>
                        <TableCell>
                          {sync.error_message ? (
                            <Typography variant="caption" color="error">
                              {sync.error_message}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No sync status available. Trigger a sync to see status.
                </Typography>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : syncHistory.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Trigger</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="right">Holdings</TableCell>
                      <TableCell align="right">Transactions</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncHistory.map((sync) => (
                      <TableRow key={sync.id} hover>
                        <TableCell>{getStatusChip(sync.sync_status)}</TableCell>
                        <TableCell>
                          <Chip label={sync.sync_type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{sync.sync_trigger}</Typography>
                        </TableCell>
                        <TableCell>
                          {formatDateTime(sync.sync_started_at, { includeTime: true })}
                        </TableCell>
                        <TableCell>
                          {sync.sync_completed_at
                            ? formatDateTime(sync.sync_completed_at, { includeTime: true })
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {sync.sync_duration_ms !== undefined
                            ? `${(sync.sync_duration_ms / 1000).toFixed(2)}s`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {sync.holdings_synced_count ?? '—'}
                        </TableCell>
                        <TableCell align="right">
                          {sync.transactions_synced_count ?? '—'}
                        </TableCell>
                        <TableCell>
                          {sync.error_message ? (
                            <Typography variant="caption" color="error">
                              {sync.error_message}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No sync history available.
                </Typography>
              </Box>
            )}
          </TabPanel>
        </Paper>

        {!selectedBrokerAccount && accounts.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              No Broker Accounts
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect a broker account to view sync status.
            </Typography>
            <Button variant="contained" href="/broker-accounts/add">
              Add Broker Account
            </Button>
          </Paper>
        )}
      </Container>
    </AuthenticatedLayout>
  );
}
